import { createSelector } from '@ngrx/store';
import { addDays, isBefore } from 'date-fns';
import { RepeatingScheduleRange, Row, ScheduleType, Slot } from 'src/app/types/aggregate-graph.types';
import { differenceInDaysIgnoreTimezone } from 'src/app/types/date-custom.types';
import { accessToken } from '../auth/auth.selectors';
import { visitsByVisitNumberDesc } from '../patient-graph/patient-graph.selectors';
import { AppState } from '../reducers';
import { sheet } from '../sheet-graph/sheet-graph.selectors';

export const selectGranularityChangeActive = (state: AppState) => state.sheet.granularityChangeActive;
export const selectSelectedGranularity = (state: AppState) => state.sheet.selectedGranularity;
export const selectGranularSlotNumber = (state: AppState) => state.sheet.granularSlotNumber;
export const selectSheetEndDate = (state: AppState) => state.sheet.sheetEndDate;
export const selectSelectorDate = (state: AppState) => state.sheet.selectorDate;
export const selectSelectedDate = (state: AppState) => state.sheet.selectedDate;
export const selectSelectedVisitId = (state: AppState) => state.sheet.selectedVisitId;
export const selectViewStatus = (state: AppState) => state.sheet.viewStatus;
export const selectViewSelector = (state: AppState) => state.sheet.viewSelector;
export const selectSheetActionNotes = (state: AppState) => state.sheet.actionNotes;
export const selectSelectedItemId = (state: AppState) => state.sheet.selectedItemId;
export const selectSelectedSlotIndex = (state: AppState) => state.sheet.selectedSlotIndex;
export const selectSelectedActionId = (state: AppState) => state.sheet.selectedActionId;
export const selectGridChangeData = (state: AppState) => state.sheet.gridChangeData;

export const granularityChangeActive = createSelector(
	selectGranularityChangeActive,
	(isActive) => isActive
);

export const selectedGranularity = createSelector(
	selectSelectedGranularity,
	(granularity) => granularity
);

export const granularSlotNumber = createSelector(
	selectGranularSlotNumber,
	(slotNumber) => slotNumber
);

export const sheetEndDate = createSelector(
	selectSheetEndDate,
	(sheetEndDate) => sheetEndDate
);

export const selectorDate = createSelector(
	selectSelectorDate,
	(selectorDate) => selectorDate
);

export const selectedDate = createSelector(
	selectSelectedDate,
	(selectedDate) => selectedDate
);

const selectedDayIndices = createSelector(
	sheet,
	selectedDate,
	(sheet, selectedDate) => {
		const dayIndex = sheet ? differenceInDaysIgnoreTimezone(selectedDate, sheet.sheetStartDate) : 0;
		const startSlotIndex = sheet ? sheet.slotsPerDay.slice(0, dayIndex).reduce((acc, val) => acc + val, 0) : 0;
		const endSlotIndex = sheet ? startSlotIndex + sheet.slotsPerDay[dayIndex] - 1 : 0;
		return { startSlotIndex, endSlotIndex };
	}
);

export interface SelectedDatePayload {
	selectedDateSlotLabels: string[];
	selectedDateRows: Row[];
}

export const selectedDatePayload = createSelector(
	sheet,
	selectedDayIndices,
	selectedDate,
	(sheet, selectedDayIndices, selectedDate): SelectedDatePayload => {

		let selectedDateRows: Row[] = [];
		let selectedDateSlotLabels: string[] = [];
		if (sheet) {
			selectedDateSlotLabels = sheet.slotLabels.slice(selectedDayIndices.startSlotIndex, selectedDayIndices.endSlotIndex + 1);
			selectedDateRows = sheet.rows.map(row => {
				const { daySlots, dayRepeatingScheduleRanges } = getDayProperties(selectedDayIndices.startSlotIndex, selectedDayIndices.endSlotIndex, row);
				const dayRow: Row = { ...row, slots: daySlots, repeatingScheduleRanges: dayRepeatingScheduleRanges };
				return {
					row: dayRow,
					// row must be shown if created on or before the selected clinical day
					// AND
					// only if it has not been removed (unless it has actions)
					// AND
					// must either: be Unscheduled/WhenRequired (PRN) schedule OR must have actions/SAs for that day
					showRow:
						isBefore(row.createdDate, addDays(selectedDate, 1))
						&&
						(!row.isRemoved || daySlots.some(slot => !!slot?.action))
						&&
						(
							Object.values(row.instructions).map(i => i.schedule.scheduleTypeName).some(s => s === ScheduleType.Unscheduled || s === ScheduleType.WhenRequired)
							||
							dayRow.slots.some(s => s?.isAction || s?.isScheduledAction || s?.isSkippedScheduledAction || s?.isGranular)
						),
				}
			}).filter(vm => vm.showRow).map(vm => vm.row).sort((a, b) => {
				// sort by group number first
				const groupOrder = a.groupNumber - b.groupNumber;
				if (groupOrder !== 0) return groupOrder;

				// then by name
				const nameA = (a.name ?? '').toUpperCase(); // ignore upper and lowercase
				const nameB = (b.name ?? '').toUpperCase(); // ignore upper and lowercase
				if (nameA < nameB) {
					return -1;
				}
				if (nameA > nameB) {
					return 1;
				}

				// fall back to numeric id sort if names are equal
				return a.id - b.id;
			});
		}

		return { selectedDateRows, selectedDateSlotLabels };
	}
);

interface DayProperties {
	daySlots: (Slot | null)[];
	dayRepeatingScheduleRanges: RepeatingScheduleRange[];
}

const getDayProperties = (startSheetSlotIndex: number, endSheetSlotIndex: number, row: Row): DayProperties => {

	// get the slots for the selected day
	const daySlots = row.slots.slice(startSheetSlotIndex, endSheetSlotIndex + 1);

	//  get the repeating schedule ranges for the selected day
	const dayRepeatingScheduleRanges: RepeatingScheduleRange[] = row.repeatingScheduleRanges.filter(range => {
		// only need ranges that overlap with the selected day
		return range.firstSlotStartIndex < endSheetSlotIndex && range.lastSlotStartIndex > startSheetSlotIndex;
	}).map(range => ({
		...range,
		firstSlotStartIndex: Math.max(range.firstSlotStartIndex, startSheetSlotIndex) - startSheetSlotIndex,
		lastSlotStartIndex: Math.min(range.lastSlotStartIndex, endSheetSlotIndex) - startSheetSlotIndex
	}
	));

	// remove any that are duplicated due to slotSpan
	// note: the server returns empty slots (e.g. 24 for a typical "hour" granularity day), even if other slots are multi span
	//		 E.g. 24 "hour" granularity slots, where one spans 2 hours, equals 25 hours! Need to remove them
	let indices: number[] = [];
	daySlots.forEach((slot, index) => { if (slot && slot.slotSpan > 1) indices.push(index) });
	indices.reverse().forEach(sheetSlotIndex => {
		// remove overlapped empty slots that are part of the slotSpan
		const removedSlotCount = daySlots.splice(sheetSlotIndex + 1, (daySlots[sheetSlotIndex] as Slot).slotSpan - 1).length;

		// adjust the repeating schedule ranges to account for the removed slots
		dayRepeatingScheduleRanges.forEach(range => {
			range.firstSlotStartIndex = adjustRangeIndexForSlotSpan(range.firstSlotStartIndex, sheetSlotIndex, removedSlotCount);
			range.lastSlotStartIndex = adjustRangeIndexForSlotSpan(range.lastSlotStartIndex, sheetSlotIndex, removedSlotCount);
		});
	});

	// the last slot might have slotSpan > 1 that pushes into the next day, which would break the UI grid (making the item/row wrap)
	if (daySlots.length) {
		const lastSlot = daySlots[daySlots.length - 1];
		if (lastSlot && lastSlot.slotSpan > 1) {
			const daySlotCount = endSheetSlotIndex + 1 - startSheetSlotIndex;
			const spannedSlotCountExclusiveOfLastSlotSpan = daySlots.slice(0, daySlots.length - 2).reduce((acc, slotOrNull) => acc + (slotOrNull?.slotSpan ?? 1), 0) + 1;
			const newSlotSpan = Math.min(lastSlot.slotSpan, daySlotCount - spannedSlotCountExclusiveOfLastSlotSpan);
			daySlots.pop();
			daySlots.push({ ...lastSlot, slotSpan: newSlotSpan });
		}
	}

	// the ui needs to know the granular slot index for each "scheduled action" slot
	let granularSlotIndex = 0;
	for (let i = 0; i < daySlots.length; i++) {
		const slot = daySlots[i];
		if (slot && (slot.isScheduledAction || slot.isGranular)) {
			daySlots[i] = { ...slot, granularSlotIndex };
		}
		granularSlotIndex += slot?.slotSpan ?? 1;
	}

	return { daySlots, dayRepeatingScheduleRanges };
};

const adjustRangeIndexForSlotSpan = (rangeIndex: number, slotIndex: number, removedSlotCount: number) => rangeIndex <= slotIndex ? rangeIndex : rangeIndex - Math.min(removedSlotCount, rangeIndex - slotIndex);

export const selectedSheetVisit = createSelector(
	selectSelectedVisitId,
	visitsByVisitNumberDesc,
	(selectedVisitId, visits) => visits.find(visit => visit.id === selectedVisitId) ?? null
);

export const selectedItem = createSelector(
	selectSelectedItemId,
	sheet,
	(selectedItemId, sheet) => sheet ? sheet.rows.find(r => r.id === selectedItemId) ?? null : null
);

export interface SelectedSlotPayload {
	selectedItem: Row | null;
	selectedSlot: Slot | null;
	defaultPerformedDate: string;
}

export const selectedSlotPayload = createSelector(
	selectedItem,
	sheet,
	selectSelectedSlotIndex,
	selectedDayIndices,
	(selectedItem, sheet, selectedSlotIndex, selectedSlotIndices): SelectedSlotPayload => {
		return {
			selectedItem,
			selectedSlot: selectedItem && selectedSlotIndex !== null ? selectedItem.slots.slice(selectedSlotIndices.startSlotIndex, selectedSlotIndices.endSlotIndex + 1)[selectedSlotIndex] : null,
			defaultPerformedDate: sheet && selectedSlotIndex !== null ? sheet.slotDefaultPerformedDates.slice(selectedSlotIndices.startSlotIndex, selectedSlotIndices.endSlotIndex + 1)[selectedSlotIndex] : ''
		};
	}
);

export const loadedActionPayload = createSelector(
	selectSelectedActionId,
	accessToken,
	(selectedActionId, accessToken) => ({ selectedActionId, accessToken })
);

export const viewStatus = createSelector(
	selectViewStatus,
	(viewStatus) => viewStatus
);

export const viewSelector = createSelector(
	selectViewSelector,
	(viewStatus) => viewStatus
);

export const sheetActionNotes = createSelector(
	selectSheetActionNotes,
	(actionNotes) => actionNotes
);

export const gridChangeData = createSelector(
	selectGridChangeData,
	(gridChangeData) => gridChangeData
);